/**
 * EoT Claim Builder — Claim Pack.
 *
 * Cognara drafts the notice letter and the narrative; the tables come from the
 * engines. Every paragraph stays editable, and the pack downloads as PDF, Word
 * or Excel.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Sparkles, FileText, FileSpreadsheet, FileType, Save, Mail } from 'lucide-react';

const SECTIONS = [
  { key: 'introduction', label: '1. Summary of the claim' },
  { key: 'contractualBasis', label: '2. The contractual basis' },
  { key: 'eventsNarrative', label: '3. The delay events' },
  { key: 'analysisNarrative', label: '5. The delay analysis' },
  { key: 'entitlementNarrative', label: '6. Entitlement to time' },
  { key: 'conclusion', label: '9. Conclusion' },
];

const DOWNLOADS = [
  { format: 'pdf', label: 'Claim pack (PDF)', icon: FileText },
  { format: 'docx', label: 'Claim pack (Word)', icon: FileType },
  { format: 'xlsx', label: 'Tables (Excel)', icon: FileSpreadsheet },
  { format: 'letter-pdf', label: 'Notice letter (PDF)', icon: Mail },
  { format: 'letter-docx', label: 'Notice letter (Word)', icon: Mail },
];

export default function ClaimPackTab({ claimId, onChanged }) {
  const [state, setState] = useState(null);
  const [narrative, setNarrative] = useState({});
  const [letter, setLetter] = useState({ subject: '', addressee: '', body: [] });
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    const { data } = await api.get(`/eot/claims/${claimId}/pack`);
    setState(data);
    if (data.pack) {
      setNarrative(data.pack.narrative || {});
      setLetter(data.pack.letter || { subject: '', addressee: '', body: [] });
    }
  }, [claimId]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const generate = async () => {
    setBusy('generate');
    try {
      const { data } = await api.post(`/eot/claims/${claimId}/pack/generate`, {});
      setNarrative(data.narrative || {});
      setLetter(data.letter || {});
      toast.success('Claim pack drafted — read it through and edit anything you would say differently');
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not draft the pack'); }
    finally { setBusy(''); }
  };

  const save = async () => {
    setBusy('save');
    try {
      await api.patch(`/eot/claims/${claimId}/pack`, { narrative, letter });
      toast.success('Your wording saved');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get(`/eot/claims/${claimId}/pack/download?format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = (res.headers['content-disposition'] || '').split('filename=')[1]?.replaceAll('"', '')
        || `claim-pack.${format.includes('docx') ? 'docx' : format === 'xlsx' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not build that file'); }
    finally { setBusy(''); }
  };

  if (!state) return <div className="pt-6 text-xs text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4 pt-4" data-testid="eot-pack-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-foreground font-medium">Claim pack</p>
          <p className="text-[11px] text-muted-foreground">
            A formal notice letter and a full narrative report with the calculation tables and appendices.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={generate} disabled={busy === 'generate' || !state.ready}
                  data-testid="eot-generate-pack" className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]">
            {busy === 'generate' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            {state.pack ? 'Re-draft' : 'Draft the pack'}
          </Button>
          {state.pack && (
            <Button size="sm" variant="outline" onClick={save} disabled={busy === 'save'} data-testid="eot-save-pack">
              {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Save wording
            </Button>
          )}
        </div>
      </div>

      {!state.ready && (
        <p className="text-xs text-amber-600" data-testid="eot-pack-blocked">
          The pack needs a delay analysis run and a saved entitlement position first.
        </p>
      )}

      {state.stale && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="eot-pack-stale">
          <p className="text-xs text-amber-600">{state.staleReason}</p>
        </div>
      )}

      {state.pack && (
        <>
          <div className="flex flex-wrap gap-2" data-testid="eot-pack-downloads">
            {DOWNLOADS.map(d => (
              <Button key={d.format} size="sm" variant="outline" onClick={() => download(d.format)}
                      disabled={busy === d.format} data-testid={`eot-download-${d.format}`}>
                {busy === d.format ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <d.icon className="w-3.5 h-3.5 mr-1.5" />}
                {d.label}
              </Button>
            ))}
          </div>

          <div className="rounded-lg border border-border p-3 space-y-2" data-testid="eot-letter-editor">
            <p className="text-xs text-foreground font-medium">Notice letter</p>
            <Textarea rows={3} value={letter.addressee || ''} data-testid="eot-letter-addressee"
                      onChange={e => setLetter(p => ({ ...p, addressee: e.target.value }))}
                      placeholder="Addressee" className="text-xs" />
            <Input value={letter.subject || ''} data-testid="eot-letter-subject"
                   onChange={e => setLetter(p => ({ ...p, subject: e.target.value }))}
                   placeholder="Subject" className="h-8 text-xs" />
            <Textarea rows={10} value={(letter.body || []).join('\n\n')} data-testid="eot-letter-body"
                      onChange={e => setLetter(p => ({ ...p, body: e.target.value.split(/\n\s*\n/) }))}
                      className="text-xs" />
          </div>

          <div className="rounded-lg border border-border p-3 space-y-3" data-testid="eot-narrative-editor">
            <p className="text-xs text-foreground font-medium">Narrative report</p>
            {SECTIONS.map(s => (
              <div key={s.key}>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{s.label}</p>
                <Textarea rows={5} value={narrative[s.key] || ''} data-testid={`eot-narrative-${s.key}`}
                          onChange={e => setNarrative(p => ({ ...p, [s.key]: e.target.value }))}
                          className="text-xs" />
              </div>
            ))}
          </div>

          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Badge variant="outline" className="text-[9px]">AI drafted, you own the words</Badge>
            Drafted {String(state.pack.generatedAt).slice(0, 16).replace('T', ' ')} by {state.pack.generatedBy}
            {state.pack.editedAt ? ` · edited ${String(state.pack.editedAt).slice(0, 16).replace('T', ' ')} by ${state.pack.editedBy}` : ''}
            . The tables and every figure in the documents come from the engines, not the draft.
          </p>
        </>
      )}
    </div>
  );
}
